import React, { useCallback } from 'react';
import {
  Box,
  HStack,
  VStack,
  Text,
  Badge,
  Menu,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import { IconButton } from 'src/components/atoms/IconButton';
import { MenuButton } from 'src/components/atoms/MenuButton';
import { MenuList } from 'src/components/molecules/MenuList';
import { MenuOption } from 'src/components/atoms/MenuOption';
import { ModelListItem } from 'src/api/models';
import { formatMediaList } from 'src/features/Settings/utils/formatters';
import { formatKpiUnit } from 'src/utils/kpiUnit';
import { useCurrency } from 'src/hooks/useCurrency';
import { MODEL_STATUS_CONFIG } from 'src/features/Settings/utils/constants';
import { dayjs } from 'src/libs/dayjs';

// nextRunAt is ISO 8601 UTC; shown in UTC with an explicit marker so the
// value reads the same for every user regardless of browser timezone.
const formatNextRun = (nextRunAt: string | null | undefined): string =>
  nextRunAt ? `${dayjs(nextRunAt).utc().format('DD.MM.YYYY HH:mm')} UTC` : '-';

type ModelCardProps = {
  model: ModelListItem;
  onQuickView: (model: ModelListItem) => void;
  onEdit: (model: ModelListItem) => void;
  onDelete: (model: ModelListItem) => void;
};

export const ModelCard: React.FC<ModelCardProps> = ({
  model,
  onQuickView,
  onEdit,
  onDelete,
}) => {
  const statusConfig = MODEL_STATUS_CONFIG[model.status];
  const secondaryTextColor = useColorModeValue('gray.600', 'gray.400');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { code: currencyCode } = useCurrency();

  const handleQuickView = useCallback(() => {
    onClose();
    onQuickView(model);
  }, [onClose, onQuickView, model]);

  const handleEdit = useCallback(() => {
    onClose();
    onEdit(model);
  }, [onClose, onEdit, model]);

  const handleDelete = useCallback(() => {
    onClose();
    onDelete(model);
  }, [onClose, onDelete, model]);

  return (
    <Box
      bg="surface.panel"
      borderRadius="12px"
      border="1px solid"
      borderColor="surface.border"
      p={5}
      _hover={{ borderColor: 'gray.300', shadow: 'sm' }}
      transition="all 0.2s">
      {/* Header: Name + Status + Menu */}
      <HStack justify="space-between" align="flex-start" mb={4}>
        <HStack spacing={3} align="center">
          <Text fontFamily="marketingPage" fontSize="lg" color="content.title">
            {model.modelName}
          </Text>
          <Badge
            px={2}
            py={0.5}
            borderRadius="md"
            fontSize="xs"
            fontWeight="medium"
            textTransform="none"
            variant="outline"
            color={secondaryTextColor}>
            {model.quickRun ? 'Quick' : 'Full'}
          </Badge>
          <Badge
            px={2}
            py={0.5}
            borderRadius="md"
            fontSize="xs"
            fontWeight="medium"
            textTransform="none"
            bg={statusConfig.bg}
            color={statusConfig.color}>
            {statusConfig.label}
          </Badge>
        </HStack>

        <Menu isOpen={isOpen} onOpen={onOpen} onClose={onClose}>
          <MenuButton
            buttonName="model-card-menu"
            as={IconButton}
            aria-label="Model actions"
            bg="transparent"
            p={0}
            minW="auto"
            h="auto"
            _hover={{ bg: 'transparent' }}
            _active={{ bg: 'transparent' }}>
            <Box
              as="span"
              fontSize="20px"
              lineHeight="1"
              color={secondaryTextColor}>
              ⋯
            </Box>
          </MenuButton>
          <MenuList buttonName="model-card-menu">
            <MenuOption
              buttonName="model-quick-view"
              label="Quick View"
              value="quick-view"
              isSelected={false}
              onClick={handleQuickView}
            />
            <MenuOption
              buttonName="model-edit"
              label="Edit"
              value="edit"
              isSelected={false}
              onClick={handleEdit}
            />
            <MenuOption
              buttonName="model-delete"
              label="Delete"
              value="delete"
              isSelected={false}
              onClick={handleDelete}
              color="red.500"
            />
          </MenuList>
        </Menu>
      </HStack>

      {/* Details: Version, Metric, Category, Media, Campaigns */}
      <VStack align="stretch" spacing={2}>
        <HStack justify="space-between">
          <Text fontSize="sm" color="content.muted">
            Version:
          </Text>
          <Text fontSize="sm" color="content.title" fontWeight="medium">
            {(model.versionNumber ?? 0) > 0 ? `v${model.versionNumber}` : '-'}
          </Text>
        </HStack>
        <HStack justify="space-between">
          <Text fontSize="sm" color="content.muted">
            Next run:
          </Text>
          <Text fontSize="sm" color="content.title" fontWeight="medium">
            {formatNextRun(model.nextRunAt)}
          </Text>
        </HStack>
        <HStack justify="space-between">
          <Text fontSize="sm" color="content.muted">
            Metric:
          </Text>
          <Text fontSize="sm" color="content.title" fontWeight="medium">
            {model.kpiMetric || '-'}
          </Text>
        </HStack>
        <HStack justify="space-between">
          <Text fontSize="sm" color="content.muted">
            Unit:
          </Text>
          <Text fontSize="sm" color="content.title" fontWeight="medium">
            {formatKpiUnit(model.kpiUnit, currencyCode)}
          </Text>
        </HStack>
        <HStack justify="space-between">
          <Text fontSize="sm" color="content.muted">
            Media:
          </Text>
          <Text fontSize="sm" color="content.title" fontWeight="medium">
            {formatMediaList(model.mediaPlatforms)}
          </Text>
        </HStack>
        <HStack justify="space-between">
          <Text fontSize="sm" color="content.muted">
            Campaigns:
          </Text>
          <Text fontSize="sm" color="content.title" fontWeight="medium">
            {model.campaignCount || 0}
          </Text>
        </HStack>
      </VStack>
    </Box>
  );
};
